// Московское время плана. МСК — UTC+3 без перехода на летнее время с 2014 года,
// поэтому хватает сдвига на 3 часа, без Intl и таблиц поясов.
// День — строка "YYYY-MM-DD" (московский календарный день), так их хранит сервер.

export type Day = string

const MSK_MS = 3 * 3_600_000
const DAY_MS = 86_400_000
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function fromUtcDate(d: Date): Day {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** Полночь дня в UTC — для арифметики дней, это не момент времени. */
export function dayMs(day: Day): number {
  const m = DAY_RE.exec(day)
  if (!m) return Number.NaN
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

export function isDay(value: unknown): value is Day {
  if (typeof value !== 'string' || !DAY_RE.test(value)) return false
  const ms = dayMs(value)
  return Number.isFinite(ms) && fromUtcDate(new Date(ms)) === value
}

export function mskDayOfMs(ms: number): Day {
  return fromUtcDate(new Date(ms + MSK_MS))
}

export function mskDay(iso: string): Day {
  return mskDayOfMs(new Date(iso).getTime())
}

export function mskTime(iso: string): string {
  const d = new Date(new Date(iso).getTime() + MSK_MS)
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

/** Московские день и время → ISO в UTC (`…Z`), как ждёт сервер. */
export function mskToIso(day: Day, time = '12:00'): string {
  const [hh = '12', mm = '00'] = time.split(':')
  return new Date(dayMs(day) + (Number(hh) * 60 + Number(mm)) * 60_000 - MSK_MS).toISOString()
}

/** Тот же час по Москве, другой день: перенос в календаре. */
export function moveToDay(iso: string, day: Day): string {
  return mskToIso(day, mskTime(iso))
}

export function todayMsk(now: number = Date.now()): Day {
  return mskDayOfMs(now)
}

export function addDays(day: Day, n: number): Day {
  return fromUtcDate(new Date(dayMs(day) + n * DAY_MS))
}

/** Сколько дней от `a` до `b`: b − a. */
export function diffDays(a: Day, b: Day): number {
  return Math.round((dayMs(b) - dayMs(a)) / DAY_MS)
}

/** 0 — понедельник, 6 — воскресенье. */
export function weekdayIndex(day: Day): number {
  return (new Date(dayMs(day)).getUTCDay() + 6) % 7
}

export function mondayOf(day: Day): Day {
  return addDays(day, -weekdayIndex(day))
}

export function isWeekend(day: Day): boolean {
  return weekdayIndex(day) >= 5
}

export function firstOfMonth(day: Day): Day {
  return `${day.slice(0, 7)}-01`
}

export function addMonths(day: Day, n: number): Day {
  const d = new Date(dayMs(firstOfMonth(day)))
  d.setUTCMonth(d.getUTCMonth() + n)
  return fromUtcDate(d)
}

/** Дни от `from` до `to`, `to` не включается. */
export function daysBetween(from: Day, to: Day): Day[] {
  const out: Day[] = []
  for (let d = from; d < to; d = addDays(d, 1)) out.push(d)
  return out
}

export function maxDay(a: Day, b: Day): Day {
  return a > b ? a : b
}

export function minDay(a: Day, b: Day): Day {
  return a < b ? a : b
}


const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
const MONTH_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]
const MONTH_NOM = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]
export const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function parts(day: Day): { y: number; m: number; d: number } {
  return { y: Number(day.slice(0, 4)), m: Number(day.slice(5, 7)) - 1, d: Number(day.slice(8, 10)) }
}

/** «24 окт». */
export function formatDay(day: Day): string {
  const { m, d } = parts(day)
  return `${d} ${MONTH_SHORT[m]}`
}

/** «24 октября». */
export function formatDayLong(day: Day): string {
  const { m, d } = parts(day)
  return `${d} ${MONTH_GEN[m]}`
}

/** «Пн, 20 окт». */
export function formatWeekday(day: Day): string {
  return `${WEEKDAY_SHORT[weekdayIndex(day)]}, ${formatDay(day)}`
}

/** «Октябрь 2026». */
export function formatMonth(day: Day): string {
  const { y, m } = parts(day)
  return `${MONTH_NOM[m]} ${y}`
}

export function monthShort(day: Day): string {
  return MONTH_SHORT[parts(day).m] ?? ''
}

export function monthName(day: Day): string {
  return MONTH_NOM[parts(day).m] ?? ''
}

/** «24–27 окт», «30 окт – 2 ноя»; один день — «24 окт». */
export function formatRange(start: Day, end: Day): string {
  if (start === end) return formatDay(start)
  const a = parts(start)
  const b = parts(end)
  if (a.m === b.m && a.y === b.y) return `${a.d}–${b.d} ${MONTH_SHORT[b.m]}`
  return `${formatDay(start)} – ${formatDay(end)}`
}

/** «20–26 октября», «27 октября – 2 ноября». */
export function formatWeekRange(start: Day, end: Day): string {
  const a = parts(start)
  const b = parts(end)
  if (a.m === b.m) return `${a.d}–${b.d} ${MONTH_GEN[b.m]}`
  return `${formatDayLong(start)} – ${formatDayLong(end)}`
}

/** «28 окт, 15:00» по Москве. */
export function formatPublish(iso: string): string {
  return `${formatDay(mskDay(iso))}, ${mskTime(iso)}`
}

/** «27 окт, 18:40» — момент из истории. */
export const formatMoment = formatPublish
